/**
 * Returns the prevailing axis orientation of the room polygons as a unit vector.
 *
 * For every polygon segment, the vector (p1 - p0) is snapped to the nearest of
 * the four 90° rotations of the current accumulated orientation, then added
 * (weighted by its length) to the accumulator.  The result is the dominant
 * axis direction across all rooms.
 *
 * Returns [1, 0] when no valid segments are found.
 */
export function computePrevailingOrientation(rooms) {
    let ox = 0;
    let oy = 0;
    for (const room of rooms) {
        const poly = room.polygon;
        for (let i = 0; i + 1 < poly.length; i++) {
            const p0 = poly[i];
            const p1 = poly[i + 1];
            const dx = (p1[0] ?? 0) - (p0[0] ?? 0);
            const dy = (p1[1] ?? 0) - (p0[1] ?? 0);
            if (Math.hypot(dx, dy) < 1e-9)
                continue;
            // The four 90° rotations of (dx, dy).
            const rotations = [
                [dx, dy],
                [-dy, dx],
                [-dx, -dy],
                [dy, -dx],
            ];
            // Seed orientation with first valid segment so dot products are meaningful.
            if (ox === 0 && oy === 0) {
                ox = dx;
                oy = dy;
                continue;
            }
            // Pick the rotation most aligned with current orientation.
            let bestDot = -Infinity;
            let bestDx = dx;
            let bestDy = dy;
            for (const [rx, ry] of rotations) {
                const dot = rx * ox + ry * oy;
                if (dot > bestDot) {
                    bestDot = dot;
                    bestDx = rx;
                    bestDy = ry;
                }
            }
            ox += bestDx;
            oy += bestDy;
        }
    }
    const len = Math.hypot(ox, oy);
    return len < 1e-9 ? [1, 0] : [ox / len, oy / len];
}
/**
 * Projects a 2D point from world coordinates into the frame whose X axis is
 * `xAxis` (a unit vector).  The Y axis is xAxis rotated 90° CCW: [-uy, ux].
 *
 *   x' =  px·ux + py·uy
 *   y' = -px·uy + py·ux
 */
export function projectPoint(point, xAxis) {
    const [px, py] = point;
    const [ux, uy] = xAxis;
    return [px * ux + py * uy, -px * uy + py * ux];
}
/**
 * Inverse of `projectPoint`: transforms a point from the rotated frame back
 * into world coordinates.
 *
 *   px = rx·ux - ry·uy
 *   py = rx·uy + ry·ux
 */
export function unprojectPoint(point, xAxis) {
    const [rx, ry] = point;
    const [ux, uy] = xAxis;
    return [rx * ux - ry * uy, rx * uy + ry * ux];
}
export function clampAutoPipeRoutingIterationCount(iterationCount, minIterations, maxIterations) {
    if (minIterations > maxIterations) {
        throw new Error("minIterations cannot be greater than maxIterations");
    }
    return Math.min(Math.max(iterationCount, minIterations), maxIterations);
}
